/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useCallback, useEffect, useMemo } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { colors, fonts, mixins } from '@blend/consumer-components';
import { BrandColors } from '@blend/enterprise-components';
import { useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import { useRoom } from '../../../hooks/useRoom';
import { mediaBreakpoints, mediaOrientations } from '../../../common/responsive';
import { zIndex } from '../../../style/zIndex';
import { Logo } from '../../../components/common/Logo/Logo';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { setSelectedNavigationItemId } from '../../../actions/signingRoomActions';
import { useAdmissionNotifications } from '../hooks/useAdmissionNotifications';
import { notaryNavigationItems, participantNavigationItems, SigningRoomNavigationItemIds } from './signingRoomItems';
import { SigningRoomNavigationExitItem } from './desktop/SigningRoomNavigationExitItem';
import { SigningRoomNavigationItem } from './SigningRoomNavigationItem';
import { diagnostics } from '@utils/diagnostics';
import { isNotaryRoute, isSigningRoomRoute } from '@utils/routing';

export namespace SigningRoomNavigation {
  export interface Props {
    startCall?(): Promise<void>;
    startVideoRecording?(): Promise<void>;
    handleDocumentClick(documentId: string): void;
    handleEditDocumentClick?(documentId: string): void;
    handleFinalizeDocumentClick?(documentId: string): void;
  }
}

const styles = StyleSheet.create({
  wrapper: {
    width: 352,
    height: '100%',
    display: 'flex',
    boxShadow: mixins.boxShadow(0, 4, 4, 0, mixins.fade(colors.black, 0.25)),
    flexDirection: 'column',
    zIndex: zIndex.sidebar,
    [mediaBreakpoints.mobile]: {
      width: '100%',
      height: 52,
      ...mixins.borderTop(2, 'solid', colors.catalystLight03),
      [mediaOrientations.landscape]: {
        width: 52,
        height: '100%',
      },
    },
  },
  logoWrapper: {
    backgroundColor: colors.white,
    display: 'flex',
    alignItems: 'center',
    height: 80,
    [mediaBreakpoints.mobile]: {
      display: 'none',
    },
  },
  navigationWrapper: {
    display: 'flex',
    flexGrow: 1,
    [mediaBreakpoints.mobile]: {
      flexDirection: 'column-reverse',
      [mediaOrientations.landscape]: {
        height: '100%',
      },
    },
  },
  navigationIcons: {
    width: 48,
    height: '100%',
    backgroundColor: colors.catalyst,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    [mediaBreakpoints.mobile]: {
      display: 'flex',
      backgroundColor: colors.white,
      [mediaOrientations.portrait]: {
        flexDirection: 'row',
        width: '100%',
        ...mixins.padding(8, 11),
        justifyContent: 'space-between',
      },
      [mediaOrientations.landscape]: {
        flexDirection: 'column',
        width: 52,
        height: '100%',
        justifyContent: 'center',
      },
    },
  },
  activityWrapper: {
    display: 'flex',
    backgroundColor: colors.white,
    flexGrow: 1,
    position: 'relative',
    ...mixins.borderTop(1, 'solid', mixins.globalBorderColor),
    [mediaBreakpoints.mobile]: {
      display: 'none',
    },
  },
  activityContentWrapper: {
    backgroundColor: BrandColors.WHITE,
    overflowY: 'scroll',
    ...mixins.fillContainer(),
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: colors.white,
    fontSize: 18,
    height: 48,
    ...fonts.UntitledSans,
    ...mixins.padding(0, 0, 0, 8),
  },
  exitButton: {
    marginTop: 'auto', // This aligns the exit button on the bottom for desktop
  },
});

export const SigningRoomNavigation: React.FC<SigningRoomNavigation.Props> = ({
  handleDocumentClick,
  handleEditDocumentClick,
  handleFinalizeDocumentClick,
  startCall,
  startVideoRecording,
}) => {
  const dispatch = useDispatch();
  const { roomId } = useRoom();
  const location = useLocation();
  const { documents, users, selectedNavigationItemId, isMobileView } = useRootSelector(state => ({
    documents: state.document.documents,
    users: state.signingRoom.users,
    selectedNavigationItemId: state.signingRoom.selectedNavigationItemId,
    isMobileView: state.layout.isMobileView,
  }));
  const isNotary = isNotaryRoute(location.pathname);
  useAdmissionNotifications(isNotary);

  const navigationItems = useMemo(() => (isNotary ? notaryNavigationItems : participantNavigationItems), [isNotary]);

  useEffect(() => {
    const defaultSelectedNavigationItem = navigationItems.find(
      item =>
        item.id === (isMobileView ? SigningRoomNavigationItemIds.documents : SigningRoomNavigationItemIds.participants),
    );
    if (!!defaultSelectedNavigationItem) {
      setSelectedNavigationItemId(defaultSelectedNavigationItem.id)(dispatch);
    } else {
      diagnostics.error(new Error('No selected navigation item'), { navigationItems });
    }
  }, []);

  useEffect(() => {
    if (isMobileView) {
      const documentsNavigationItem = navigationItems.find(item => item.id === SigningRoomNavigationItemIds.documents);
      if (!!documentsNavigationItem) {
        setSelectedNavigationItemId(documentsNavigationItem.id)(dispatch);
      } else {
        diagnostics.error(new Error(`Can't find documents navigation item from navigationItems`), { navigationItems });
      }
    }
  }, [isMobileView]);

  const onNavigationItemClick = useCallback(
    (id: string) => {
      setSelectedNavigationItemId(id)(dispatch);
    },
    [dispatch],
  );

  const selectedNavigationItem = useMemo(
    () => navigationItems.find(navigationItem => navigationItem.id === selectedNavigationItemId) || navigationItems[0],
    [selectedNavigationItemId, navigationItems],
  );
  const SidebarContentComponent = useMemo(() => selectedNavigationItem?.contentComponent, [selectedNavigationItem]);
  const isExitVisible = useMemo(
    () => !isMobileView && isSigningRoomRoute(location.pathname),
    [location.pathname, isMobileView],
  );
  const getCount = (navigationItemId: string) => {
    switch (navigationItemId) {
      case 'documents':
        return documents.length;
      case 'participants':
        return users.inVideoCall.reduce((total, user) => total + (user?.users?.length || 0), 0);
      default:
        return undefined;
    }
  };

  return (
    <div className={css(styles.wrapper)} data-it="SigningRoomNavigation">
      <div className={css(styles.logoWrapper)}>
        <Logo />
      </div>
      <div className={css(styles.navigationWrapper)}>
        <div className={css(styles.navigationIcons)}>
          {navigationItems.map(navigationItem => {
            const isSelected = !!selectedNavigationItem ? navigationItem.id === selectedNavigationItem.id : false;
            return (
              <SigningRoomNavigationItem
                id={navigationItem.id}
                title={navigationItem.title}
                onClick={onNavigationItemClick}
                count={getCount(navigationItem.id)}
                isSelected={isSelected}
                key={navigationItem.id}
              />
            );
          })}
          {isExitVisible && (
            <div className={css(styles.exitButton)}>
              <SigningRoomNavigationExitItem />
            </div>
          )}
        </div>
        <div className={css(styles.activityWrapper)}>
          <div className={css(styles.activityContentWrapper)}>
            <div className={css(styles.titleWrapper)} data-dd-privacy="allow">
              {selectedNavigationItem?.title}
            </div>
            <SidebarContentComponent
              isNotary={isNotary}
              roomId={roomId}
              handleDocumentClick={handleDocumentClick}
              handleEditDocumentClick={handleEditDocumentClick}
              handleFinalizeDocumentClick={handleFinalizeDocumentClick}
              startCall={startCall}
              startVideoRecording={startVideoRecording}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
